/**
 * Orbi-Ausdrücke — portiert aus dem `orbi-animation-lab` (tools/,
 * externes Motion-Lab, nicht Teil dieses Repos). Freigegeben (Marcel,
 * 25./26.09.2026): laugh, hop-small, hop, hop-left, hop-right, hop-zigzag,
 * giggle-bounce, hop-laugh, spin-centrifugal, idle-whistle. `hop` und
 * `hop-small` laufen je dreimal hintereinander (`JUMP_REPEATS`).
 *
 * Pixelwerte an `actor`/`shadow` (HTML) rechnet `scalePx` vom 220px-Lab-Orb
 * auf die echte Orb-Größe um; SVG-interne Werte bleiben unverändert.
 *
 * `spin-centrifugal` läuft mit den Speed Lines der Lab-Bühne (zwei SVG-
 * Ebenen hinter/vor dem Orb, proportional zur Orb-Größe) — ohne sie las sich
 * die Drehung auf 48–64px nur als seitlich wegrutschendes Gesicht.
 *
 * `idle-whistle` bekommt ihre drei Notenglyphen (Lab: fünf, bis zu
 * translate(210px,-205px) scale(2.15) — für eine ~194px-Bühne gedacht, gut
 * das Doppelte der eigenen Höhe). Die Dashboard-Kachel schneidet mit
 * `overflow: hidden` schon ~11–14px hinter dem Orb-Rand ab
 * (tutor-card.component.scss); ein 1:1-Import der Lab-Distanzen würde mitten
 * im Flug abgeschnitten statt auszublenden. `createWhistleNoteFrames` skaliert
 * deshalb relativ zu `elements.svgSize` (Aufstieg 55%, Streuung 35% davon) —
 * bewusst unter dem rechnerisch sicheren Rahmen (Mundposition bis Orb-Rand
 * plus Kachel-Restfläche), damit auch die Glyphengröße selbst noch Platz hat.
 * Bei einem größeren Einsatzort (Kandidat: Chat-Widget) lohnt sich ein
 * zweiter, weniger eingeschränkter Parametersatz.
 */

/** Reine Web-Animations-Keyframes — kein DOM-Bezug, direkt aus dem Lab
 *  übernommen (nur `Keyframe[]`-Typisierung ergänzt). */
type Frames = Keyframe[];

/** Kantenlänge des Lab-Orbs (`--orbi-size` in core/orbi.css). */
const LAB_ORB_SIZE = 220;

/** Die Lab-Keyframes für `actor` und `shadow` stehen in echten Pixeln,
 *  gemessen an einem 220px-Orb. Beide sind hier HTML-Elemente, also ebenfalls
 *  echte Pixel — auf einem 48–64px-Orb sprang der große Hüpfer mit -62px
 *  damit rund eine volle Orb-Höhe und verließ die Kachel. Skaliert auf die
 *  tatsächliche Orb-Größe stimmen die Proportionen wieder mit dem Lab
 *  überein. SVG-interne Ziele (Augen, Mund, Bänder) brauchen das nicht:
 *  deren Pixel sind viewBox-Einheiten, und der viewBox ist identisch. */
function scalePx(frames: Frames, svgSize: number): Frames {
  const factor = svgSize / LAB_ORB_SIZE;
  return frames.map((frame) =>
    typeof frame['transform'] === 'string'
      ? {
          ...frame,
          transform: frame['transform'].replace(
            /(-?\d*\.?\d+)px/g,
            (_, value: string) => `${(parseFloat(value) * factor).toFixed(2)}px`,
          ),
        }
      : frame,
  );
}

function transformDirectional({
  x = 0,
  y = 0,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
}: {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
}): string {
  return `translateX(${x}px) translateY(${y}px) scale(${scaleX}, ${scaleY}) rotate(${rotation}deg)`;
}

// ---------------------------------------------------------------------
// Hüpfer — core/hop-keyframes.js
// ---------------------------------------------------------------------

function createClassicHopFrames(): { body: Frames; shadow: Frames } {
  return {
    body: [
      { transform: 'translateY(0) scale(1, 1)', offset: 0 },
      { transform: 'translateY(3px) scale(1.12, .88)', offset: 0.08, easing: 'cubic-bezier(.3,.05,.7,.4)' },
      { transform: 'translateY(-62px) scale(.93, 1.10)', offset: 0.26, easing: 'cubic-bezier(.15,.72,.28,1)' },
      { transform: 'translateY(5px) scale(1.16, .84)', offset: 0.44, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: 'translateY(-14px) scale(.98, 1.03)', offset: 0.52, easing: 'cubic-bezier(.16,.78,.3,1)' },
      { transform: 'translateY(0) scale(1, 1)', offset: 0.62, easing: 'cubic-bezier(.16,1,.3,1)' },
      { transform: 'translateY(0) scale(1, 1)', offset: 1 },
    ],
    shadow: [
      { transform: 'scaleX(1)', opacity: 0.28, offset: 0 },
      { transform: 'scaleX(1.14)', opacity: 0.34, offset: 0.08 },
      { transform: 'scaleX(.42)', opacity: 0.08, offset: 0.26 },
      { transform: 'scaleX(1.24)', opacity: 0.4, offset: 0.44 },
      { transform: 'scaleX(.72)', opacity: 0.17, offset: 0.52 },
      { transform: 'scaleX(1)', opacity: 0.28, offset: 0.62 },
      { transform: 'scaleX(1)', opacity: 0.28, offset: 1 },
    ],
  };
}

function createSmallHopFrames(): { body: Frames; shadow: Frames } {
  return {
    body: [
      { transform: 'translateY(0) scale(1, 1)', offset: 0 },
      { transform: 'translateY(1px) scale(1.055, .945)', offset: 0.14, easing: 'cubic-bezier(.3,.05,.7,.4)' },
      { transform: 'translateY(-18px) scale(.975, 1.045)', offset: 0.42, easing: 'cubic-bezier(.14,.72,.28,1)' },
      { transform: 'translateY(2px) scale(1.075, .925)', offset: 0.68, easing: 'cubic-bezier(.5,.02,.78,.48)' },
      { transform: 'translateY(0) scale(1, 1)', offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
    ],
    shadow: [
      { transform: 'scaleX(1)', opacity: 0.28, offset: 0 },
      { transform: 'scaleX(1.06)', opacity: 0.31, offset: 0.14 },
      { transform: 'scaleX(.72)', opacity: 0.18, offset: 0.42 },
      { transform: 'scaleX(1.12)', opacity: 0.35, offset: 0.68 },
      { transform: 'scaleX(1)', opacity: 0.28, offset: 1 },
    ],
  };
}

// ---------------------------------------------------------------------
// Gerichtete Hüpfer — core/directional-hop-keyframes.js
// ---------------------------------------------------------------------

function createDirectionalHopFrames(direction: -1 | 1): { body: Frames; shadow: Frames } {
  const x = (value: number) => value * direction;
  const rotation = (value: number) => value * direction;

  return {
    body: [
      { transform: transformDirectional({}), offset: 0 },
      { transform: transformDirectional({ y: 3, scaleX: 1.08, scaleY: 0.92 }), offset: 0.05, easing: 'cubic-bezier(.4,0,.75,.35)' },
      { transform: transformDirectional({ x: x(16), y: -26, scaleX: 0.97, scaleY: 1.06, rotation: rotation(2.4) }), offset: 0.14, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: x(30), y: 2, scaleX: 1.09, scaleY: 0.91, rotation: rotation(0.8) }), offset: 0.22, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: x(30), y: 3, scaleX: 1.075, scaleY: 0.925, rotation: rotation(0.4) }), offset: 0.28 },
      { transform: transformDirectional({ x: x(47), y: -29, scaleX: 0.965, scaleY: 1.07, rotation: rotation(2.8) }), offset: 0.38, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: x(62), y: 2, scaleX: 1.1, scaleY: 0.9, rotation: rotation(0.8) }), offset: 0.47, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: x(62), scaleX: 1.04, scaleY: 0.96, rotation: rotation(0.3) }), offset: 0.56 },
      { transform: transformDirectional({ x: x(31), y: -32, scaleX: 0.96, scaleY: 1.075, rotation: rotation(-2.6) }), offset: 0.68, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ y: 3, scaleX: 1.12, scaleY: 0.88 }), offset: 0.8, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ y: -6, scaleX: 0.99, scaleY: 1.02 }), offset: 0.89, easing: 'cubic-bezier(.16,.78,.3,1)' },
      { transform: transformDirectional({}), offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
    ],
    shadow: [
      { transform: `translateX(0px) scaleX(1)`, opacity: 0.28, offset: 0 },
      { transform: `translateX(0px) scaleX(1.10)`, opacity: 0.33, offset: 0.05 },
      { transform: `translateX(${x(16)}px) scaleX(.66)`, opacity: 0.15, offset: 0.14 },
      { transform: `translateX(${x(30)}px) scaleX(1.15)`, opacity: 0.36, offset: 0.22 },
      { transform: `translateX(${x(30)}px) scaleX(1.10)`, opacity: 0.33, offset: 0.28 },
      { transform: `translateX(${x(47)}px) scaleX(.62)`, opacity: 0.14, offset: 0.38 },
      { transform: `translateX(${x(62)}px) scaleX(1.17)`, opacity: 0.37, offset: 0.47 },
      { transform: `translateX(${x(62)}px) scaleX(1.04)`, opacity: 0.3, offset: 0.56 },
      { transform: `translateX(${x(31)}px) scaleX(.58)`, opacity: 0.13, offset: 0.68 },
      { transform: `translateX(0px) scaleX(1.20)`, opacity: 0.39, offset: 0.8 },
      { transform: `translateX(0px) scaleX(.84)`, opacity: 0.21, offset: 0.89 },
      { transform: `translateX(0px) scaleX(1)`, opacity: 0.28, offset: 1 },
    ],
  };
}

function createZigzagHopFrames(): { body: Frames; shadow: Frames } {
  return {
    body: [
      { transform: transformDirectional({}), offset: 0 },
      { transform: transformDirectional({ y: 4, scaleX: 1.13, scaleY: 0.87 }), offset: 0.035, easing: 'cubic-bezier(.4,0,.75,.35)' },
      { transform: transformDirectional({ x: -32, y: -58, scaleX: 0.93, scaleY: 1.1, rotation: -3.2 }), offset: 0.105, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: -64, y: 4, scaleX: 1.16, scaleY: 0.84, rotation: -0.7 }), offset: 0.18, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: -64, y: 5, scaleX: 1.11, scaleY: 0.89, rotation: -0.3 }), offset: 0.21 },
      { transform: transformDirectional({ x: 0, y: -65, scaleX: 0.92, scaleY: 1.12, rotation: 3.6 }), offset: 0.29, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: 64, y: 4, scaleX: 1.17, scaleY: 0.83, rotation: 0.7 }), offset: 0.36, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: 64, y: 5, scaleX: 1.11, scaleY: 0.89, rotation: 0.3 }), offset: 0.39 },
      { transform: transformDirectional({ x: 0, y: -63, scaleX: 0.92, scaleY: 1.12, rotation: -3.6 }), offset: 0.47, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: -64, y: 4, scaleX: 1.17, scaleY: 0.83, rotation: -0.7 }), offset: 0.54, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: -64, y: 5, scaleX: 1.11, scaleY: 0.89, rotation: -0.3 }), offset: 0.57 },
      { transform: transformDirectional({ x: 0, y: -65, scaleX: 0.92, scaleY: 1.12, rotation: 3.6 }), offset: 0.65, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ x: 64, y: 4, scaleX: 1.17, scaleY: 0.83, rotation: 0.7 }), offset: 0.72, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ x: 64, y: 5, scaleX: 1.11, scaleY: 0.89, rotation: 0.3 }), offset: 0.75 },
      { transform: transformDirectional({ x: 32, y: -56, scaleX: 0.935, scaleY: 1.1, rotation: -2.8 }), offset: 0.84, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { transform: transformDirectional({ y: 4, scaleX: 1.18, scaleY: 0.82 }), offset: 0.92, easing: 'cubic-bezier(.48,.02,.78,.5)' },
      { transform: transformDirectional({ y: -8, scaleX: 0.985, scaleY: 1.025 }), offset: 0.965, easing: 'cubic-bezier(.16,.78,.3,1)' },
      { transform: transformDirectional({}), offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
    ],
    shadow: [
      { transform: 'translateX(0px) scaleX(1)', opacity: 0.28, offset: 0 },
      { transform: 'translateX(0px) scaleX(1.16)', opacity: 0.36, offset: 0.035 },
      { transform: 'translateX(-32px) scaleX(.45)', opacity: 0.09, offset: 0.105 },
      { transform: 'translateX(-64px) scaleX(1.24)', opacity: 0.4, offset: 0.18 },
      { transform: 'translateX(-64px) scaleX(1.17)', opacity: 0.36, offset: 0.21 },
      { transform: 'translateX(0px) scaleX(.40)', opacity: 0.08, offset: 0.29 },
      { transform: 'translateX(64px) scaleX(1.25)', opacity: 0.41, offset: 0.36 },
      { transform: 'translateX(64px) scaleX(1.17)', opacity: 0.36, offset: 0.39 },
      { transform: 'translateX(0px) scaleX(.41)', opacity: 0.08, offset: 0.47 },
      { transform: 'translateX(-64px) scaleX(1.25)', opacity: 0.41, offset: 0.54 },
      { transform: 'translateX(-64px) scaleX(1.17)', opacity: 0.36, offset: 0.57 },
      { transform: 'translateX(0px) scaleX(.40)', opacity: 0.08, offset: 0.65 },
      { transform: 'translateX(64px) scaleX(1.25)', opacity: 0.41, offset: 0.72 },
      { transform: 'translateX(64px) scaleX(1.17)', opacity: 0.36, offset: 0.75 },
      { transform: 'translateX(32px) scaleX(.46)', opacity: 0.1, offset: 0.84 },
      { transform: 'translateX(0px) scaleX(1.27)', opacity: 0.42, offset: 0.92 },
      { transform: 'translateX(0px) scaleX(.78)', opacity: 0.18, offset: 0.965 },
      { transform: 'translateX(0px) scaleX(1)', opacity: 0.28, offset: 1 },
    ],
  };
}

// ---------------------------------------------------------------------
// Lachen — core/expression-keyframes.js (offen/lachend-Augen-Übergang)
// ---------------------------------------------------------------------

interface SmileExpressionOptions {
  enterStart?: number;
  enterCross?: number;
  enterEnd?: number;
  exitStart?: number;
  exitCross?: number;
  exitEnd?: number;
  settleEnd?: number;
  smileHoldFrames?: Keyframe[];
}

function createSmileExpressionFrames({
  enterStart = 0.1,
  enterCross = 0.135,
  enterEnd = 0.17,
  exitStart = 0.84,
  exitCross = 0.875,
  exitEnd = 0.91,
  settleEnd = 0.95,
  smileHoldFrames = [],
}: SmileExpressionOptions = {}): { openEyes: Frames; smileEyes: Frames } {
  return {
    openEyes: [
      { opacity: 1, transform: 'scaleY(1)', offset: 0 },
      { opacity: 1, transform: 'scaleY(1)', offset: enterStart },
      { opacity: 0.28, transform: 'scaleY(.62)', offset: enterCross, easing: 'cubic-bezier(.4,0,.7,1)' },
      { opacity: 0, transform: 'scaleY(.44)', offset: enterEnd },
      { opacity: 0, transform: 'scaleY(.44)', offset: exitStart },
      { opacity: 0.34, transform: 'scaleY(.68)', offset: exitCross, easing: 'cubic-bezier(.12,.72,.25,1)' },
      { opacity: 1, transform: 'scaleY(1.06)', offset: exitEnd, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'scaleY(1)', offset: settleEnd },
      { opacity: 1, transform: 'scaleY(1)', offset: 1 },
    ],
    smileEyes: [
      { opacity: 0, transform: 'scale(.84, .68) translateY(2px)', offset: 0 },
      { opacity: 0, transform: 'scale(.84, .68) translateY(2px)', offset: enterStart },
      { opacity: 0.68, transform: 'scale(.96, .90) translateY(.5px)', offset: enterCross, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'scale(1.04, 1.04) translateY(0)', offset: enterEnd },
      ...smileHoldFrames,
      { opacity: 1, transform: 'scale(1, 1) translateY(0)', offset: exitStart },
      { opacity: 0.48, transform: 'scale(.97, .82) translateY(1px)', offset: exitCross, easing: 'cubic-bezier(.4,0,.8,.45)' },
      { opacity: 0, transform: 'scale(.91, .62) translateY(2px)', offset: exitEnd },
      { opacity: 0, transform: 'scale(.91, .62) translateY(2px)', offset: settleEnd },
      { opacity: 0, transform: 'scale(.91, .62) translateY(2px)', offset: 1 },
    ],
  };
}

// ---------------------------------------------------------------------
// Zentrifugal-Drehung — core/centrifugal-spin-keyframes.js
// (Speed-Line-Frames aus dem Lab bewusst NICHT portiert, siehe Kopfkommentar)
// ---------------------------------------------------------------------

const CENTRIFUGAL_TURN_ENDS = [0.346667, 0.573333, 0.8] as const;
const CENTRIFUGAL_TURN_START = 0.12;
const CENTRIFUGAL_TURN_LENGTH = (0.8 - CENTRIFUGAL_TURN_START) / CENTRIFUGAL_TURN_ENDS.length;
const centrifugalOffsetAt = (turn: number, phase: number) =>
  Number((CENTRIFUGAL_TURN_START + CENTRIFUGAL_TURN_LENGTH * (turn + phase)).toFixed(6));

function createFaceOrbitFrames(amplitude: number): Frames {
  const frames: Keyframe[] = [
    { transform: 'translateX(0px) scaleX(1)', opacity: 1, offset: 0 },
    { transform: 'translateX(0px) scaleX(1)', opacity: 1, offset: 0.08 },
  ];

  for (let turn = 0; turn < 3; turn += 1) {
    if (turn === 0) {
      frames.push({ transform: 'translateX(0px) scaleX(1)', opacity: 1, offset: centrifugalOffsetAt(turn, 0) });
    }
    frames.push(
      { transform: `translateX(${amplitude * 0.72}px) scaleX(.72)`, opacity: 1, offset: centrifugalOffsetAt(turn, 0.18) },
      { transform: `translateX(${amplitude}px) scaleX(.12)`, opacity: 0.18, offset: centrifugalOffsetAt(turn, 0.25) },
      { transform: `translateX(${amplitude * 1.06}px) scaleX(.06)`, opacity: 0, offset: centrifugalOffsetAt(turn, 0.31) },
      { transform: `translateX(${-amplitude * 1.06}px) scaleX(.06)`, opacity: 0, offset: centrifugalOffsetAt(turn, 0.69) },
      { transform: `translateX(${-amplitude}px) scaleX(.12)`, opacity: 0.18, offset: centrifugalOffsetAt(turn, 0.75) },
      { transform: `translateX(${-amplitude * 0.72}px) scaleX(.72)`, opacity: 1, offset: centrifugalOffsetAt(turn, 0.82) },
      { transform: 'translateX(0px) scaleX(1)', opacity: 1, offset: centrifugalOffsetAt(turn, 1) },
    );
  }

  frames.push(
    { transform: 'translateX(-2px) scaleX(.98)', opacity: 1, offset: 0.9 },
    { transform: 'translateX(0px) scaleX(1)', opacity: 1, offset: 1 },
  );
  return frames;
}

function createSurfaceOrbitFrames({
  amplitude,
  frontOpacity,
  backOpacity,
}: {
  amplitude: number;
  frontOpacity: number;
  backOpacity: number;
}): Frames {
  const neutral = 'translateX(0px) scaleX(1)';
  const frames: Keyframe[] = [
    { transform: neutral, opacity: frontOpacity, offset: 0 },
    { transform: neutral, opacity: frontOpacity, offset: 0.08 },
  ];

  for (let turn = 0; turn < 3; turn += 1) {
    if (turn === 0) frames.push({ transform: neutral, opacity: frontOpacity, offset: centrifugalOffsetAt(turn, 0) });
    frames.push(
      { transform: `translateX(${amplitude}px) scaleX(.42)`, opacity: frontOpacity * 0.8, offset: centrifugalOffsetAt(turn, 0.25) },
      { transform: 'translateX(0px) scaleX(-.92)', opacity: backOpacity, offset: centrifugalOffsetAt(turn, 0.5) },
      { transform: `translateX(${-amplitude}px) scaleX(.42)`, opacity: frontOpacity * 0.8, offset: centrifugalOffsetAt(turn, 0.75) },
      { transform: neutral, opacity: frontOpacity, offset: centrifugalOffsetAt(turn, 1) },
    );
  }

  frames.push({ transform: neutral, opacity: frontOpacity, offset: 1 });
  return frames;
}

function createCentrifugalSpinFrames(): {
  body: Frames;
  eyes: Frames;
  shine: Frames;
  surfaceA: Frames;
  surfaceB: Frames;
  shadow: Frames;
  glow: Frames;
  speedBack: Frames;
  speedFront: Frames;
  speedStreaks: Frames;
} {
  return {
    body: [
      { transform: 'translateY(0) scale(1, 1)', offset: 0 },
      { transform: 'translateY(0) scale(.97, 1.03)', offset: 0.08, easing: 'cubic-bezier(.4,0,.75,.35)' },
      { transform: 'translateY(0) scale(1.05, .97)', offset: 0.12, easing: 'cubic-bezier(.16,.7,.28,1)' },
      { transform: 'translateY(0) scale(1.10, .95)', offset: 0.346667 },
      { transform: 'translateY(0) scale(1.12, .94)', offset: 0.573333 },
      { transform: 'translateY(0) scale(1.08, .96)', offset: 0.8 },
      { transform: 'translateY(0) scale(.98, 1.02)', offset: 0.9, easing: 'cubic-bezier(.16,.78,.3,1)' },
      { transform: 'translateY(0) scale(1, 1)', offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
    ],
    eyes: createFaceOrbitFrames(24),
    shine: createFaceOrbitFrames(18),
    surfaceA: createSurfaceOrbitFrames({ amplitude: 23, frontOpacity: 0.94, backOpacity: 0.32 }),
    surfaceB: createSurfaceOrbitFrames({ amplitude: 18, frontOpacity: 0.72, backOpacity: 0.24 }),
    shadow: [
      { transform: 'scaleX(1)', opacity: 0.28, offset: 0 },
      { transform: 'scaleX(1.08)', opacity: 0.31, offset: 0.12 },
      { transform: 'scaleX(1.25)', opacity: 0.25, offset: 0.346667 },
      { transform: 'scaleX(1.30)', opacity: 0.23, offset: 0.573333 },
      { transform: 'scaleX(1.20)', opacity: 0.26, offset: 0.8 },
      { transform: 'scaleX(1)', opacity: 0.28, offset: 1 },
    ],
    glow: [
      { opacity: 0.48, offset: 0 },
      { opacity: 0.62, offset: 0.12 },
      { opacity: 0.78, offset: 0.346667 },
      { opacity: 0.88, offset: 0.573333 },
      { opacity: 0.7, offset: 0.8 },
      { opacity: 0.48, offset: 1 },
    ],
    speedBack: [
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(0px) scale(.84, .9)', offset: 0 },
      { opacity: 0.22, transform: 'translate(-50%, -50%) translateX(-10px) scale(.94, .96)', offset: 0.12 },
      { opacity: 0.78, transform: 'translate(-50%, -50%) translateX(12px) scale(1, 1)', offset: 0.346667 },
      { opacity: 0.96, transform: 'translate(-50%, -50%) translateX(-12px) scale(1.04, 1)', offset: 0.573333 },
      { opacity: 0.74, transform: 'translate(-50%, -50%) translateX(10px) scale(1, .98)', offset: 0.8 },
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(18px) scale(1.08, 1)', offset: 0.9 },
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(0px) scale(1, 1)', offset: 1 },
    ],
    speedFront: [
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(0px) scale(.84, .9)', offset: 0 },
      { opacity: 0.18, transform: 'translate(-50%, -50%) translateX(10px) scale(.94, .96)', offset: 0.12 },
      { opacity: 0.72, transform: 'translate(-50%, -50%) translateX(-12px) scale(1, 1)', offset: 0.346667 },
      { opacity: 0.92, transform: 'translate(-50%, -50%) translateX(12px) scale(1.04, 1)', offset: 0.573333 },
      { opacity: 0.68, transform: 'translate(-50%, -50%) translateX(-10px) scale(1, .98)', offset: 0.8 },
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(-18px) scale(1.08, 1)', offset: 0.9 },
      { opacity: 0, transform: 'translate(-50%, -50%) translateX(0px) scale(1, 1)', offset: 1 },
    ],
    // viewBox-Einheiten der Speed-Line-SVGs — nicht skalieren.
    speedStreaks: [
      { strokeDashoffset: '0px', offset: 0 },
      { strokeDashoffset: '0px', offset: 0.12 },
      { strokeDashoffset: '-180px', offset: 0.346667 },
      { strokeDashoffset: '-360px', offset: 0.573333 },
      { strokeDashoffset: '-540px', offset: 0.8 },
      { strokeDashoffset: '0px', offset: 1 },
    ],
  };
}

// ---------------------------------------------------------------------
// Idle: Pfeifen — core/personality-keyframes.js
// (Notenglyphen-Frames aus dem Lab bewusst NICHT portiert, siehe Kopfkommentar)
// ---------------------------------------------------------------------

const NEUTRAL_BODY = 'translateX(0px) translateY(0px) scale(1, 1) rotate(0deg)';

function createIdleWhistleFrames(): { body: Frames; eyes: Frames; mouthClosed: Frames } {
  return {
    body: [
      { transform: NEUTRAL_BODY, offset: 0 },
      { transform: 'translateX(-3px) translateY(-1px) scale(1.005, 1) rotate(-2deg)', offset: 0.18 },
      { transform: 'translateX(3px) translateY(-2px) scale(.998, 1.006) rotate(2deg)', offset: 0.42 },
      { transform: 'translateX(-2px) translateY(-1px) scale(1.004, .999) rotate(-1.4deg)', offset: 0.66 },
      { transform: 'translateX(2px) translateY(-2px) scale(1, 1.004) rotate(1.2deg)', offset: 0.82 },
      { transform: NEUTRAL_BODY, offset: 1 },
    ],
    eyes: [
      { transform: 'translate(0px, 0px) scale(1, 1)', offset: 0 },
      { transform: 'translate(2px, -5px) scale(1, .92)', offset: 0.16 },
      { transform: 'translate(-2px, -5px) scale(1, .92)', offset: 0.4 },
      { transform: 'translate(3px, -4px) scale(1, .94)', offset: 0.64 },
      { transform: 'translate(0px, -5px) scale(1, .92)', offset: 0.82 },
      { transform: 'translate(0px, 0px) scale(1, 1)', offset: 1 },
    ],
    // Der offene Pfeifmund (`mouth`) bleibt im Lab praktisch unsichtbar
    // (`hiddenMouth`, opacity 0 durchgehend) — nur die geschlossene,
    // pulsierende Fassung trägt die sichtbare Pfeif-Pose.
    mouthClosed: [
      { opacity: 0, transform: 'translateX(3px) scale(.82)', offset: 0 },
      { opacity: 1, transform: 'translateX(0px) scale(.82)', offset: 0.04, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(0px) scale(1.03)', offset: 0.07, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(0px) scale(1.32)', offset: 0.1, easing: 'cubic-bezier(.4,0,.6,1)' },
      { opacity: 1, transform: 'translateX(4px) scale(1.06)', offset: 0.13, easing: 'cubic-bezier(.2,.8,.2,1)' },
      { opacity: 1, transform: 'translateX(9px) scale(.80)', offset: 0.16, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(10px) scale(.88)', offset: 0.26 },
      { opacity: 1, transform: 'translateX(9px) scale(.78)', offset: 0.36 },
      { opacity: 1, transform: 'translateX(10px) scale(.86)', offset: 0.44 },
      { opacity: 0, transform: 'translateX(10px) scale(.82)', offset: 0.47 },
      { opacity: 0, transform: 'translateX(0px) scale(.82)', offset: 0.49 },
      { opacity: 1, transform: 'translateX(0px) scale(.82)', offset: 0.5, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(0px) scale(1.03)', offset: 0.53, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(0px) scale(1.32)', offset: 0.56, easing: 'cubic-bezier(.4,0,.6,1)' },
      { opacity: 1, transform: 'translateX(4px) scale(1.06)', offset: 0.59, easing: 'cubic-bezier(.2,.8,.2,1)' },
      { opacity: 1, transform: 'translateX(9px) scale(.80)', offset: 0.62, easing: 'cubic-bezier(.16,1,.3,1)' },
      { opacity: 1, transform: 'translateX(10px) scale(.88)', offset: 0.7 },
      { opacity: 1, transform: 'translateX(9px) scale(.78)', offset: 0.8 },
      { opacity: 1, transform: 'translateX(10px) scale(.88)', offset: 0.9 },
      { opacity: 0, transform: 'translateX(10px) scale(.82)', offset: 0.94 },
      { opacity: 0, transform: 'translateX(0) scale(.82)', offset: 1 },
    ],
  };
}

// ---------------------------------------------------------------------
// Pfeif-Noten — core/personality-keyframes.js::createWhistleNoteFrames,
// Distanzen relativ zu `svgSize` statt Lab-Fixwerten (siehe Kopfkommentar).
// ---------------------------------------------------------------------

/** Ein Keyframe der Notenbahn. `x`/`y` sind zusätzliche Pixel-Verschiebung
 *  ÜBER dem `translate(-50%, -50%)`-Zentrierungs-Präfix (siehe Template/SCSS-
 *  Kommentar zu `.agent-orb__note`) — Web-Animations-Keyframes ersetzen
 *  `transform` vollständig, deshalb trägt jeder Frame die volle Kette. */
function noteFrame(
  x: number,
  y: number,
  rotation: number,
  scale: number,
  opacity: number,
  offset: number,
  easing?: string,
): Keyframe {
  return {
    opacity,
    transform: `translate(-50%, -50%) translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${rotation}deg) scale(${scale})`,
    offset,
    ...(easing ? { easing } : {}),
  };
}

function createWhistleNoteFrames(index: number, svgSize: number): Frames {
  const direction = index % 2 ? 1 : -1;
  const riseY = -0.55 * svgSize;
  const spreadX = 0.35 * svgSize;

  const burst = (enter: number, driftX: number, compact: boolean): Keyframe[] => {
    const riseOffset = compact ? 0.05 : 0.08;
    const midOffset = compact ? 0.08 : 0.16;
    const exitOffset = compact ? 0.12 : 0.24;
    return [
      noteFrame(driftX, 0, 0, 0.55, 0, enter),
      noteFrame(driftX + direction * spreadX * 0.18, riseY * 0.14, direction * 4, 0.75, 1, enter + 0.03, 'cubic-bezier(.16,1,.3,1)'),
      noteFrame(driftX + direction * spreadX * 0.5, riseY * 0.55, direction * 8, 1.05, 1, enter + riseOffset, 'cubic-bezier(.22,.72,.28,1)'),
      noteFrame(driftX + direction * spreadX * 0.8, riseY * 0.85, direction * 12, 1.2, 0.85, enter + midOffset),
      noteFrame(driftX + direction * spreadX, riseY, direction * 15, 1, 0, Math.min(0.98, enter + exitOffset)),
    ];
  };

  const firstEnter = 0.18 + index * 0.05;
  const secondEnter = 0.64 + index * 0.045;
  return [
    noteFrame(0, 0, 0, 0.55, 0, 0),
    ...burst(firstEnter, 0, false),
    ...burst(secondEnter, spreadX * 0.06, true),
    noteFrame(direction * spreadX, riseY, direction * 15, 1, 0, 1),
  ];
}

// =======================================================================
// Öffentliche Sequenz-Registry
// =======================================================================

/** Elemente, die eine Sequenz animieren darf — Teilmenge dessen, was
 *  `AgentOrbComponent` intern über `@ViewChild` bereitstellt. Kein Element
 *  ist für jede Sequenz Pflicht; jede `play()`-Funktion nutzt nur, was sie
 *  braucht. */
export interface OrbiSequenceElements {
  actor: HTMLElement;
  shadow: HTMLElement;
  eyes: SVGGElement;
  surfaceA: SVGGElement;
  surfaceB: SVGGElement;
  openEyes: SVGGElement;
  smileEyes: SVGGElement;
  glow: SVGCircleElement;
  shine: SVGGElement;
  mouthWhistleClosed: SVGCircleElement;
  /** Pfeif-Noten (`.agent-orb__note` × 3) für `idle-whistle`. */
  notes: HTMLElement[];
  /** Speed-Line-Ebenen hinter/vor dem Orb und ihre Striche (`spin-centrifugal`). */
  speedBack: SVGSVGElement;
  speedFront: SVGSVGElement;
  speedPathsBack: SVGPathElement[];
  speedPathsFront: SVGPathElement[];
  /** Kantenlänge des gerenderten SVGs (`AgentOrbComponent.svgSize`) — für
   *  Sequenzen, deren Distanzen sich am tatsächlichen Orb-Maßstab statt an
   *  Lab-Fixwerten orientieren müssen (siehe `createWhistleNoteFrames`). */
  svgSize: number;
}

/** Signatur von `AgentOrbComponent`s eigenem `animate()`-Wrapper — hängt
 *  Tempo/`prefers-reduced-motion` genauso ein wie der Controller im Lab. */
export type OrbiAnimateFn = (
  element: Element,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions
) => Animation;

export interface OrbiSequenceMeta {
  id: string;
  label: string;
  duration: number;
}

export interface OrbiSequence {
  meta: OrbiSequenceMeta;
  play(elements: OrbiSequenceElements, animate: OrbiAnimateFn): Animation[];
}

function bodyAndShadow(
  frames: { body: Frames; shadow: Frames },
  duration: number,
  iterations = 1,
): OrbiSequence['play'] {
  return (elements, animate) => {
    const options: KeyframeAnimationOptions = { duration, iterations, easing: 'linear', fill: 'both' };
    return [
      animate(elements.actor, scalePx(frames.body, elements.svgSize), options),
      animate(elements.shadow, scalePx(frames.shadow, elements.svgSize), options),
    ];
  };
}

/** "Springen" läuft immer dreimal hintereinander (Marcel, 26.09.2026). */
const JUMP_REPEATS = 3;

export const ORBI_SEQUENCES: OrbiSequence[] = [
  {
    meta: { id: 'laugh', label: 'Lachen', duration: 1600 },
    play(elements, animate) {
      const expression = createSmileExpressionFrames({
        enterStart: 0.09,
        enterCross: 0.13,
        enterEnd: 0.17,
        exitStart: 0.84,
        exitCross: 0.875,
        exitEnd: 0.91,
        settleEnd: 0.95,
        smileHoldFrames: [
          { opacity: 1, transform: 'scale(.98, .94) translateY(1px)', offset: 0.38 },
          { opacity: 1, transform: 'scale(1.04, 1.04) translateY(0)', offset: 0.54 },
          { opacity: 1, transform: 'scale(1, 1) translateY(0)', offset: 0.78 },
        ],
      });
      const duration = 1600;
      return [
        animate(elements.actor, scalePx([
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 0 },
          { transform: 'translateY(3px) scale(1.045, .955) rotate(0deg)', offset: 0.1, easing: 'cubic-bezier(.35,0,.65,1)' },
          { transform: 'translateY(-5px) scale(.985, 1.035) rotate(-2deg)', offset: 0.23, easing: 'cubic-bezier(.2,.8,.3,1)' },
          { transform: 'translateY(1px) scale(1.025, .98) rotate(2deg)', offset: 0.36 },
          { transform: 'translateY(-4px) scale(.99, 1.025) rotate(-1.5deg)', offset: 0.49 },
          { transform: 'translateY(1px) scale(1.018, .985) rotate(1.2deg)', offset: 0.62 },
          { transform: 'translateY(-2px) scale(.997, 1.012) rotate(-.6deg)', offset: 0.74 },
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
        ], elements.svgSize), { duration, easing: 'linear', fill: 'both' }),
        animate(elements.openEyes, expression.openEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.smileEyes, expression.smileEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.glow, [
          { opacity: 0.48, offset: 0 },
          { opacity: 0.72, offset: 0.2 },
          { opacity: 0.62, offset: 0.58 },
          { opacity: 0.48, offset: 1 },
        ], { duration, easing: 'ease-in-out', fill: 'both' }),
      ];
    },
  },
  {
    meta: { id: 'hop-small', label: 'Kleiner Hüpfer', duration: 620 * JUMP_REPEATS },
    play: bodyAndShadow(createSmallHopFrames(), 620, JUMP_REPEATS),
  },
  {
    meta: { id: 'hop', label: 'Großer Hüpfer', duration: 1600 * JUMP_REPEATS },
    play: bodyAndShadow(createClassicHopFrames(), 1600, JUMP_REPEATS),
  },
  {
    meta: { id: 'hop-left', label: 'Zweimal nach links', duration: 2350 },
    play: bodyAndShadow(createDirectionalHopFrames(-1), 2350),
  },
  {
    meta: { id: 'hop-right', label: 'Zweimal nach rechts', duration: 2350 },
    play: bodyAndShadow(createDirectionalHopFrames(1), 2350),
  },
  {
    meta: { id: 'hop-zigzag', label: 'Großes Zickzack', duration: 4200 },
    play: bodyAndShadow(createZigzagHopFrames(), 4200),
  },
  {
    meta: { id: 'hop-laugh', label: 'Hüpfen + Lachen', duration: 1900 },
    play(elements, animate) {
      const expression = createSmileExpressionFrames({
        enterStart: 0.1,
        enterCross: 0.15,
        enterEnd: 0.2,
        exitStart: 0.83,
        exitCross: 0.86,
        exitEnd: 0.9,
        settleEnd: 0.94,
        smileHoldFrames: [{ opacity: 1, transform: 'scale(1, 1) translateY(0)', offset: 0.78 }],
      });
      const duration = 1900;
      return [
        animate(elements.actor, scalePx([
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 0 },
          { transform: 'translateY(4px) scale(1.09, .91) rotate(0deg)', offset: 0.1, easing: 'cubic-bezier(.5,0,.8,.4)' },
          { transform: 'translateY(-17px) scale(.965, 1.055) rotate(-2deg)', offset: 0.22, easing: 'cubic-bezier(.12,.72,.24,1)' },
          { transform: 'translateY(-34px) scale(.975, 1.035) rotate(1.5deg)', offset: 0.34 },
          { transform: 'translateY(-30px) scale(.99, 1.02) rotate(2deg)', offset: 0.43 },
          { transform: 'translateY(-12px) scale(1.015, .985) rotate(-1deg)', offset: 0.56, easing: 'cubic-bezier(.5,0,.8,.5)' },
          { transform: 'translateY(3px) scale(1.105, .895) rotate(0deg)', offset: 0.66 },
          { transform: 'translateY(-10px) scale(.985, 1.025) rotate(.8deg)', offset: 0.76, easing: 'cubic-bezier(.15,.8,.25,1)' },
          { transform: 'translateY(1px) scale(1.035, .97) rotate(-.4deg)', offset: 0.87 },
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 1, easing: 'cubic-bezier(.16,1,.3,1)' },
        ], elements.svgSize), { duration, easing: 'linear', fill: 'both' }),
        animate(elements.shadow, [
          { transform: 'scaleX(1)', opacity: 0.28, offset: 0 },
          { transform: 'scaleX(1.12)', opacity: 0.34, offset: 0.1 },
          { transform: 'scaleX(.56)', opacity: 0.13, offset: 0.34 },
          { transform: 'scaleX(.62)', opacity: 0.15, offset: 0.43 },
          { transform: 'scaleX(1.15)', opacity: 0.36, offset: 0.66 },
          { transform: 'scaleX(.82)', opacity: 0.2, offset: 0.76 },
          { transform: 'scaleX(1)', opacity: 0.28, offset: 1 },
        ], { duration, easing: 'ease-in-out', fill: 'both' }),
        animate(elements.openEyes, expression.openEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.smileEyes, expression.smileEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.glow, [
          { opacity: 0.48, offset: 0 },
          { opacity: 0.76, offset: 0.28 },
          { opacity: 0.68, offset: 0.72 },
          { opacity: 0.48, offset: 1 },
        ], { duration, easing: 'ease-in-out', fill: 'both' }),
      ];
    },
  },
  {
    meta: { id: 'giggle-bounce', label: 'Kicher-Hüpfer', duration: 1760 },
    play(elements, animate) {
      const expression = createSmileExpressionFrames({
        enterStart: 0.025,
        enterCross: 0.05,
        enterEnd: 0.075,
        exitStart: 0.86,
        exitCross: 0.89,
        exitEnd: 0.92,
        settleEnd: 0.96,
        smileHoldFrames: [
          { opacity: 1, transform: 'scale(.98, .94) translateY(1px)', offset: 0.3 },
          { opacity: 1, transform: 'scale(1.045, 1.04) translateY(0)', offset: 0.56 },
          { opacity: 1, transform: 'scale(1, 1) translateY(0)', offset: 0.8 },
        ],
      });
      const duration = 1760;
      return [
        animate(elements.actor, scalePx([
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 0 },
          { transform: 'translateY(2px) scale(1.07, .93) rotate(0deg)', offset: 0.05 },
          { transform: 'translateY(-14px) scale(.98, 1.04) rotate(-1.6deg)', offset: 0.11 },
          { transform: 'translateY(2px) scale(1.075, .925) rotate(.8deg)', offset: 0.17 },
          { transform: 'translateY(-11px) scale(.985, 1.035) rotate(1.8deg)', offset: 0.23 },
          { transform: 'translateY(2px) scale(1.07, .93) rotate(-.8deg)', offset: 0.29 },
          { transform: 'translateY(-16px) scale(.975, 1.05) rotate(-1.2deg)', offset: 0.35 },
          { transform: 'translateY(2px) scale(1.08, .92) rotate(.8deg)', offset: 0.41 },
          { transform: 'translateY(-12px) scale(.985, 1.04) rotate(1.5deg)', offset: 0.48 },
          { transform: 'translateY(2px) scale(1.07, .93) rotate(-.6deg)', offset: 0.55 },
          { transform: 'translateY(-14px) scale(.98, 1.045) rotate(-1deg)', offset: 0.62 },
          { transform: 'translateY(2px) scale(1.075, .925) rotate(.5deg)', offset: 0.69 },
          { transform: 'translateY(-5px) scale(.995, 1.018) rotate(0deg)', offset: 0.77 },
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 0.84 },
          { transform: 'translateY(0) scale(1, 1) rotate(0deg)', offset: 1 },
        ], elements.svgSize), { duration, easing: 'ease-in-out', fill: 'both' }),
        animate(elements.shadow, [
          { transform: 'scaleX(1)', opacity: 0.28, offset: 0 },
          { transform: 'scaleX(1.12)', opacity: 0.34, offset: 0.05 },
          { transform: 'scaleX(.70)', opacity: 0.17, offset: 0.11 },
          { transform: 'scaleX(1.13)', opacity: 0.35, offset: 0.17 },
          { transform: 'scaleX(.76)', opacity: 0.19, offset: 0.23 },
          { transform: 'scaleX(1.12)', opacity: 0.34, offset: 0.29 },
          { transform: 'scaleX(.67)', opacity: 0.15, offset: 0.35 },
          { transform: 'scaleX(1.14)', opacity: 0.36, offset: 0.41 },
          { transform: 'scaleX(.74)', opacity: 0.18, offset: 0.48 },
          { transform: 'scaleX(1.12)', opacity: 0.34, offset: 0.55 },
          { transform: 'scaleX(.70)', opacity: 0.17, offset: 0.62 },
          { transform: 'scaleX(1.13)', opacity: 0.35, offset: 0.69 },
          { transform: 'scaleX(.88)', opacity: 0.23, offset: 0.77 },
          { transform: 'scaleX(1)', opacity: 0.28, offset: 0.84 },
          { transform: 'scaleX(1)', opacity: 0.28, offset: 1 },
        ], { duration, easing: 'ease-in-out', fill: 'both' }),
        animate(elements.openEyes, expression.openEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.smileEyes, expression.smileEyes, { duration, easing: 'linear', fill: 'both' }),
        animate(elements.glow, [
          { opacity: 0.48, offset: 0 },
          { opacity: 0.76, offset: 0.12 },
          { opacity: 0.62, offset: 0.3 },
          { opacity: 0.78, offset: 0.49 },
          { opacity: 0.64, offset: 0.68 },
          { opacity: 0.48, offset: 1 },
        ], { duration, easing: 'ease-in-out', fill: 'both' }),
      ];
    },
  },
  {
    meta: { id: 'spin-centrifugal', label: 'Zentrifugal-Drehung', duration: 1750 },
    play(elements, animate) {
      const frames = createCentrifugalSpinFrames();
      const duration = 1750;
      const options: KeyframeAnimationOptions = { duration, easing: 'linear', fill: 'both' };
      return [
        animate(elements.actor, frames.body, options),
        animate(elements.eyes, frames.eyes, options),
        animate(elements.shine, frames.shine, options),
        animate(elements.surfaceA, frames.surfaceA, options),
        animate(elements.surfaceB, frames.surfaceB, options),
        animate(elements.shadow, frames.shadow, options),
        animate(elements.glow, frames.glow, options),
        animate(elements.speedBack, scalePx(frames.speedBack, elements.svgSize), options),
        animate(elements.speedFront, scalePx(frames.speedFront, elements.svgSize), options),
        ...elements.speedPathsBack.map((path) => animate(path, frames.speedStreaks, options)),
        ...elements.speedPathsFront.map((path) => animate(path, frames.speedStreaks.map((frame) => ({
          ...frame,
          strokeDashoffset: `${-parseFloat(String(frame['strokeDashoffset']))}px`,
        })), options)),
      ];
    },
  },
  {
    meta: { id: 'idle-whistle', label: 'Idle: Pfeifen', duration: 6800 },
    play(elements, animate) {
      const frames = createIdleWhistleFrames();
      const duration = 6800;
      const options: KeyframeAnimationOptions = { duration, easing: 'linear', fill: 'both' };
      return [
        animate(elements.actor, scalePx(frames.body, elements.svgSize), options),
        animate(elements.eyes, frames.eyes, options),
        animate(elements.mouthWhistleClosed, frames.mouthClosed, options),
        ...elements.notes.map((note, index) =>
          animate(note, createWhistleNoteFrames(index, elements.svgSize), options)
        ),
      ];
    },
  },
];

export const ORBI_SEQUENCE_IDS = ORBI_SEQUENCES.map((s) => s.meta.id);

const sequenceById = new Map(ORBI_SEQUENCES.map((s) => [s.meta.id, s]));

export function getOrbiSequence(id: string): OrbiSequence | undefined {
  return sequenceById.get(id);
}
